import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

/**
 * Token lookup priority: sessionStorage (impersonation tab) → localStorage (primary login).
 * When impersonating, the admin's tab is untouched and a new tab owns its own session.
 */
function readToken() {
  return sessionStorage.getItem("cr_token") || localStorage.getItem("cr_token");
}
function isImpersonation() {
  return !!sessionStorage.getItem("cr_token");
}

// Consume #imp=<jwt> fragment once on boot (opened from Admin → View as).
function consumeImpersonationFragment() {
  if (typeof window === "undefined") return;
  const h = window.location.hash || "";
  const m = h.match(/[#&]imp=([^&]+)/);
  if (!m) return;
  sessionStorage.setItem("cr_token", decodeURIComponent(m[1]));
  const cleaned = h.replace(/([#&])imp=[^&]+&?/, "$1").replace(/[#&]$/, "");
  window.history.replaceState(null, "", window.location.pathname + window.location.search + cleaned);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [impBy, setImpBy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    consumeImpersonationFragment();
    const t = readToken();
    if (!t) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      setImpBy(r.data?._imp_by || null);
    }).catch(() => {
      sessionStorage.removeItem("cr_token");
      localStorage.removeItem("cr_token");
    }).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("cr_token", r.data.token);
    sessionStorage.removeItem("cr_token");
    setUser(r.data.user);
    setImpBy(null);
    return r.data.user;
  };
  const register = async (payload) => {
    // Auto-attach the referral code captured by /r/<code> if present.
    const refCode = sessionStorage.getItem("ref_code");
    const body = refCode ? { ...payload, ref_code: refCode } : payload;
    const r = await api.post("/auth/register", body);
    if (refCode) sessionStorage.removeItem("ref_code");
    localStorage.setItem("cr_token", r.data.token);
    sessionStorage.removeItem("cr_token");
    setUser(r.data.user);
    setImpBy(null);
    return r.data.user;
  };
  const logout = () => {
    sessionStorage.removeItem("cr_token");
    if (!isImpersonation()) localStorage.removeItem("cr_token");
    setUser(null);
    setImpBy(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser, impBy, isImpersonating: !!impBy }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
